import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ChevronDown } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import useWaitlist from "@/modules/eventRegistration/features/EventWaitlist/hooks/useWaitlist";
import { AttendanceStatus } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchWaitlistDetails/types";
import NavigationTitleRow from "@/modules/eventRegistration/features/EventWaitlist/components/NavigationTitleRow/NavigationTitleRow";
import SessionInformationCard from "@/modules/eventRegistration/features/EventWaitlist/components/SessionInformationCard/SessionInformationCard";
import RosterTab from "@/modules/eventRegistration/features/EventWaitlist/components/RosterTab/RosterTab";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import ConfirmRemoveWaitlistModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmRemoveWaitlistModal/ConfirmRemoveWaitlistModal";
import ConfirmRegisterWaitlistModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmRegisterWaitlistModal/ConfirmRegisterWaitlistModal";
import ConfirmMoveEnrollmentToWaitlistModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmMoveEnrollmentToWaitlistModal/ConfirmMoveEnrollmentToWaitlistModal";
import ConfirmReplaceEnrollmentModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmReplaceEnrollmentModal/ConfirmReplaceEnrollmentModal";
import Attendances from "@/modules/eventRegistration/features/EventWaitlist/components/Attendances/Attendances";
import FinalizeSessionModal from "@/modules/eventRegistration/features/EventWaitlist/components/FinalizeSessionModal/FinalizeSessionModal";
import useEventTransactions from "@/modules/eventRegistration/features/EventWaitlist/hooks/useEventTransactions/useEventTransactions";
import { TransactionsView } from "@/modules/eventRegistration/features/EventWaitlist/components/TransactionsView";

interface TabOption {
  value: string;
  label: string;
  badge: number;
}

export default function Waitlist() {
  const { uuid } = useParams<{ uuid: string; eventUuid: string }>();
  const isMobile = useIsMobile();
  const [isTabSheetOpen, setIsTabSheetOpen] = useState(false);

  const {
    activeTab,
    isRefreshing,
    isProcessing,
    waitlist,
    registeredUsers,
    waitlistDetails,
    loadingAttendances,
    attendanceDates,
    currentAttendances,
    selectedAttendanceDate,
    selectedAttendanceStatusId,
    setSelectedAttendanceDate,
    setSelectedAttendanceStatusId,
    showRemoveDialog,
    showMoveToWaitlistDialog,
    showReplaceDialog,
    showRegisterDialog,
    selectedUser,
    selectedRegistration,
    handleTabChange,
    refreshData,
    removeFromWaitlist,
    registerFromWaitlist,
    moveFromEnrollmentToWaitlist,
    updateWaitlistPosition,
    moveWaitlistItemToTop,
    formatDate,
    setShowRemoveDialog,
    setShowMoveToWaitlistDialog,
    setShowReplaceDialog,
    setShowRegisterDialog,
    handleRegisterClick,
    handleRemoveClick,
    handleReplaceClick,
    handleMoveToWaitlistClick,
    handleExportAttendanceCsv,
    isExportingCsv,
    updateAttendance,
    bulkUpdateAttendanceStatus,
    isUpdatingAttendance,
    showFinalizeSessionDialog,
    setShowFinalizeSessionDialog,
    finalizeSession,
  } = useWaitlist({
    eventSessionUuid: uuid,
  });

  const {
    registrationGroups,
    isLoading: isTransactionsLoading,
    refreshData: refreshTransactions,
  } = useEventTransactions({ eventUuid: uuid });

  const isSessionFull =
    !!waitlistDetails && waitlistDetails.availableSeatCount <= 0;
  const availableSpots = waitlistDetails
    ? waitlistDetails.availableSeatCount
    : 0;

  // For the waitlist user
  const selectedUserFullName = selectedUser
    ? [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(" ")
    : "Unknown";

  // For the enrollment user we are demoting
  const selectedRegistrationFullName = selectedRegistration
    ? [selectedRegistration.firstName, selectedRegistration.lastName]
        .filter(Boolean)
        .join(" ")
    : "Unknown";

  // Tab options
  const tabOptions: TabOption[] = [
    {
      value: "roster",
      label: "Roster",
      badge: registeredUsers.length + waitlist.length,
    },
    {
      value: "attendance",
      label: "Attendance",
      badge: registeredUsers.length,
    },
    {
      value: "billing",
      label: "Billing",
      badge: registrationGroups.length,
    },
  ];

  const activeTabOption = tabOptions.find((tab) => tab.value === activeTab);

  const handleTabSelect = (value: string) => {
    handleTabChange(value);
    setIsTabSheetOpen(false);
  };

  return (
    <MainPageWrapper>
      <div className="mb-8">
        <NavigationTitleRow
          isProcessing={isProcessing}
          isRefreshing={isRefreshing}
          refreshData={refreshData}
          waitlistLength={waitlist.length}
        />
        <SessionInformationCard
          availableSpots={availableSpots}
          waitlistDetails={waitlistDetails}
          waitlistLength={waitlist.length}
        />
      </div>

      {isMobile && (
        <div className="mb-4">
          <Sheet onOpenChange={setIsTabSheetOpen} open={isTabSheetOpen}>
            <SheetTrigger asChild>
              <button
                className="w-full p-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                type="button"
              >
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gray-900 dark:text-white">
                    {activeTabOption?.label}
                  </span>
                  <Badge variant="outline">{activeTabOption?.badge}</Badge>
                </div>
                <ChevronDown className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>
            </SheetTrigger>
            <SheetContent className="h-full w-full inset-0" side="bottom">
              <div className="flex flex-col h-full">
                <SheetHeader className="pb-6">
                  <SheetTitle className="text-xl font-normal text-gray-900 dark:text-white">
                    Select Tab
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 pt-8">
                  <div className="space-y-3">
                    {tabOptions.map((tab) => (
                      <button
                        className={`w-full px-6 py-1.5 rounded-xl text-left flex items-center justify-between transition-colors text-sm ${
                          activeTab === tab.value
                            ? "bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-white border border-gray-200 dark:border-slate-700"
                            : "bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
                        }`}
                        key={tab.value}
                        onClick={() => handleTabSelect(tab.value)}
                        type="button"
                      >
                        <span className="font-medium">{tab.label}</span>
                        <Badge variant="outline">{tab.badge}</Badge>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      )}

      <Tabs
        className="w-full"
        onValueChange={handleTabSelect}
        value={activeTab}
      >
        {!isMobile && (
          <TabsList className="mb-4">
            {tabOptions.map((tab) => (
              <TabsTrigger
                className="flex items-center gap-2"
                key={tab.value}
                value={tab.value}
              >
                {tab.label}
                <Badge variant="outline">{tab.badge}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        )}

        <TabsContent value="roster">
          <RosterTab
            formatDate={formatDate}
            handleMoveToWaitlistClick={handleMoveToWaitlistClick}
            handleRegisterClick={handleRegisterClick}
            handleRemoveClick={handleRemoveClick}
            handleReplaceClick={handleReplaceClick}
            isSessionFull={isSessionFull}
            onMoveToTop={moveWaitlistItemToTop}
            onPositionChange={updateWaitlistPosition}
            registeredUsers={registeredUsers}
            waitlist={waitlist}
            waitlistDetails={waitlistDetails}
          />
        </TabsContent>

        <TabsContent value="attendance">
          <div>
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
              <div>
                <h2 className="text-base font-normal text-gray-900 dark:text-white">
                  Attendance
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Track attendance for registered users in this session.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2 justify-end">
                  <Select
                    onValueChange={(value) => {
                      setSelectedAttendanceStatusId(
                        value === "all" ? null : parseInt(value),
                      );
                    }}
                    value={selectedAttendanceStatusId?.toString() || "all"}
                  >
                    <SelectTrigger className="w-48 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors font-medium">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {waitlistDetails?.eventSessionAttendanceStatuses?.map(
                        (status: AttendanceStatus) => (
                          <SelectItem
                            key={status.id}
                            value={status.id.toString()}
                          >
                            {status.name}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>

                  <button
                    className="flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium border border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isExportingCsv}
                    onClick={handleExportAttendanceCsv}
                  >
                    {isExportingCsv ? (
                      <svg
                        className="w-4 h-4 animate-spin"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                        />
                      </svg>
                    )}
                    {isExportingCsv ? (
                      "Exporting..."
                    ) : (
                      <>
                        <span className="sm:hidden">Export</span>
                        <span className="hidden sm:inline">
                          Export Attendance
                        </span>
                      </>
                    )}
                  </button>
                </div>

                {attendanceDates.length > 0 && (
                  <div className="flex flex-wrap gap-2 justify-end">
                    {attendanceDates.map((date) => {
                      const [year, month, day] = date.split("-").map(Number);
                      const dateObj = new Date(year, month - 1, day);
                      const formattedDate = dateObj.toLocaleDateString(
                        "en-US",
                        {
                          month: "short",
                          day: "numeric",
                        },
                      );
                      return (
                        <button
                          className={
                            selectedAttendanceDate === date
                              ? "px-4 py-1.5 rounded-md text-sm font-medium border bg-gray-900 dark:bg-white text-white dark:text-gray-900 border-gray-900 dark:border-white transition-colors"
                              : "px-4 py-1.5 rounded-md text-sm font-medium border bg-white dark:bg-slate-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                          }
                          key={date}
                          onClick={() => setSelectedAttendanceDate(date)}
                        >
                          {formattedDate}
                        </button>
                      );
                    })}
                  </div>
                )}

                {!waitlistDetails?.isSessionFinalized &&
                  registeredUsers.length > 0 && (
                    <div className="flex justify-end">
                      <Button
                        onClick={() => setShowFinalizeSessionDialog(true)}
                        variant="default"
                      >
                        Finalize Session
                      </Button>
                    </div>
                  )}
              </div>
            </div>
            <Attendances
              attendances={currentAttendances || []}
              bulkUpdateAttendanceStatus={bulkUpdateAttendanceStatus}
              isSessionFinalized={waitlistDetails?.isSessionFinalized}
              isUpdatingAttendance={isUpdatingAttendance}
              loadingAttendances={loadingAttendances}
              onRefresh={refreshData}
              updateAttendance={updateAttendance}
              waitlistDetails={waitlistDetails}
            />
          </div>
        </TabsContent>

        <TabsContent value="billing">
          <TransactionsView
            isLoading={isTransactionsLoading}
            onRefundSuccess={refreshTransactions}
            registrationGroups={registrationGroups}
          />
        </TabsContent>
      </Tabs>

      {/* WAITLIST MODALS */}
      {/* REMOVE FROM WAITLIST */}
      <ConfirmRemoveWaitlistModal
        isOpen={showRemoveDialog}
        onClose={() => setShowRemoveDialog(false)}
        onConfirm={async (removalReason?: string) => {
          if (selectedUser) {
            await removeFromWaitlist(selectedUser.id, removalReason);
          }
        }}
        userFullName={selectedUserFullName}
      />

      {/* REGISTER FROM WAITLIST */}
      <ConfirmRegisterWaitlistModal
        isOpen={showRegisterDialog}
        onClose={() => setShowRegisterDialog(false)}
        onConfirm={async (sendNotification: boolean) => {
          if (selectedUser) {
            await registerFromWaitlist(selectedUser.id, sendNotification);
          }
        }}
        userFullName={selectedUserFullName}
      />

      {/* MOVE ENROLLMENT -> WAITLIST */}
      <ConfirmMoveEnrollmentToWaitlistModal
        isOpen={showMoveToWaitlistDialog}
        onClose={() => setShowMoveToWaitlistDialog(false)}
        onConfirm={async (sendNotification: boolean) => {
          if (selectedRegistration) {
            await moveFromEnrollmentToWaitlist(
              selectedRegistration.id,
              sendNotification,
            );
          }
        }}
        userFullName={selectedRegistrationFullName}
      />

      {/* REPLACE ENROLLMENT */}
      <ConfirmReplaceEnrollmentModal
        eventUuid={uuid}
        isOpen={showReplaceDialog}
        onClose={() => setShowReplaceDialog(false)}
        onSuccess={refreshData}
        selectedRegistration={selectedRegistration}
      />

      {/* FINALIZE SESSION MODAL */}
      <FinalizeSessionModal
        isOpen={showFinalizeSessionDialog}
        onClose={() => setShowFinalizeSessionDialog(false)}
        onConfirm={finalizeSession}
        waitlistCount={waitlist.length}
      />
    </MainPageWrapper>
  );
}
